/**
 * click-and-collect.js
 * ---------------------------------------------------------------------------
 * Horizon-theme Click & Collect state machine and Right-side Drawer logic.
 */

(function () {
  if (customElements.get('click-and-collect-block')) return;

  class ClickAndCollectBlock extends HTMLElement {
    constructor() {
      super();

      this.productId = this.dataset.productId;
      this.variantId = this.dataset.variantId;
      this.lowCcThreshold = parseInt(this.dataset.lowCcThreshold, 10) || 5;
      this.lowStoreThreshold = parseInt(this.dataset.lowStoreThreshold, 10) || 5;
      this.stockDisplayQueryId = this.dataset.stockDisplayQueryId;
      this.blockClickCollect = this.dataset.blockClickCollect === 'true';
      this.hasSoldOutTag = this.dataset.hasSoldOutTag === 'true';
      // Tagged intersections + sold-out: always the fully out of stock view, whatever the stock
      this.forceSoldOut = this.dataset.forceSoldOut === 'true';
      // Shopper's market country: stores only serve their own country (warehouses serve every market).
      this.countryCode = (this.dataset.countryCode || '').toUpperCase();
      this.countryName = (this.dataset.countryName || '').toLowerCase();

      // Elements
      this.mainContainer = this.querySelector('[data-main-container]');
      this.fallbackInStoreOnly = this.querySelector('[data-fallback-in-store-only]');
      this.fallbackFullyOos = this.querySelector('[data-fallback-fully-oos]');
      this.alsoAvailableStoreBtn = this.querySelector('[data-also-available-store]');

      this.tabTriggers = Array.from(this.querySelectorAll('.click-and-collect__tab-trigger'));
      this.panels = Array.from(this.querySelectorAll('.click-and-collect__panel'));

      this.storeSelectorDrawer = document.getElementById('click-collect-store-drawer-container');
      this.instoreDrawer = document.getElementById('click-collect-instore-drawer-container');

      // State
      this.activeTab = 'delivery'; // default active tab
      this.currentVariantAvailable = this.dataset.deliveryDefaultAvailable === 'true';
      if (this.hasSoldOutTag) {
        this.currentVariantAvailable = false;
      }
      this.locationsList = [];
      this.inventoryMap = {};
      this.currentView = 'main'; // 'main' | 'in-store-only' | 'fully-oos'
      // Whether the shopper's market has any pickup / in-store stores. Only set false from a
      // successful inventory response (e.g. NZ has no stores, so only Delivery applies there).
      this.marketHasPickupStores = true;
      this.marketHasStores = true;

      this.init();
    }

    async init() {
      if (!this.stockDisplayQueryId) return;

      // Sync activeTab with whatever tab is visually active in DOM on page load
      const activeTriggerInDom = this.querySelector('.click-and-collect__tab-trigger.is-active');
      if (activeTriggerInDom && activeTriggerInDom.dataset.tab) {
        this.activeTab = activeTriggerInDom.dataset.tab;
        // console.log('[CC] init: activeTab synced from DOM:', this.activeTab);
      }

      // Load initial availability
      await this.loadInventoryData();
      this.setupEventListeners();
      this.updateBlockView();
      this.setupDrawers();
      
      // Remove loading state from Add to cart button to prevent flashing
      document.documentElement.classList.add('cc-loaded');
    }

    setupEventListeners() {
      // Tab triggers
      // console.log('[CC] setupEventListeners: tabTriggers found:', this.tabTriggers.length);
      this.tabTriggers.forEach((trigger) => {
        // console.log('[CC] Tab trigger:', trigger.dataset.tab, '| is-disabled:', trigger.classList.contains('is-disabled'));
        if (!trigger.classList.contains('is-disabled')) {
          trigger.addEventListener('click', () => {
            // console.log('[CC] Tab clicked:', trigger.dataset.tab);
            this.activeTab = trigger.dataset.tab;
            this.updateTabStyles();
          });
        }
      });

      // Drawer opening buttons
      this.querySelectorAll('[data-open-store-selector], [data-change-store-cc]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.openStoreSelectorDrawer();
        });
      });

      this.querySelectorAll('[data-open-instore-selector-fallback], [data-also-available-store]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.openInstoreDrawer();
        });
      });

      // Variant change handler
      const handleVariantChange = (variant) => {
        if (variant) {
          // Clear managed state so we don't restore the old variant's button state
          const btns = this.getAtcButtons();
          if (btns) {
            btns.forEach(btn => {
              delete btn.dataset.ccManaged;
              delete btn.dataset.ccOriginalHtml;
              delete btn.dataset.ccOriginalDisabled;
              delete btn.dataset.ccOverrideText;
              delete btn.dataset.puppet;
            });
          }
          // console.log('[CC] Variant changed to:', variant.id);
          this.variantId = variant.id;
          this.currentVariantId = variant.id.toString();
          this.currentVariantAvailable = variant.available;
          if (this.hasSoldOutTag) {
            this.currentVariantAvailable = false;
          }
          this.dataset.variantId = variant.id;
          this.loadInventoryData().then(() => {
            this.updateBlockView();
            this.updateOpenDrawers();
          });
        }
      };

      // Listen for custom app variant change
      document.addEventListener('variant:change', (event) => {
        handleVariantChange(event.detail?.variant);
      });

      // Listen for native theme variant update
      document.addEventListener('variant:update', (event) => {
        handleVariantChange(event.detail?.resource);
        // Fallback: theme morphs the button synchronously in its own variant:update listener.
        // We ensure our overrides are re-applied after a short delay.
        setTimeout(() => {
          if (this.currentView !== 'main' || this.activeTab === 'click-collect') {
            this.updateAtcButton();
          }
        }, 100);
        setTimeout(() => {
          if (this.currentView !== 'main' || this.activeTab === 'click-collect') {
            this.updateAtcButton();
          }
        }, 400);
      });

      // Observe document body for ATC button replacements and text mutations
      const observer = new MutationObserver((mutations) => {
        let shouldUpdate = false;
        for (const mutation of mutations) {
          // If any child is added/removed, or if text/attributes change within the ATC button
          if (mutation.type === 'childList' || mutation.type === 'characterData' || mutation.type === 'attributes') {
            // To avoid infinite loops, ignore mutations caused by our own dataset changes
            if (mutation.type === 'attributes' && mutation.attributeName && mutation.attributeName.startsWith('data-cc-')) {
              continue;
            }
            shouldUpdate = true;
            break;
          }
        }
        if (shouldUpdate) {
          if (this._updateAtcTimeout) clearTimeout(this._updateAtcTimeout);
          this._updateAtcTimeout = setTimeout(() => {
            if (this.currentView !== 'main' || this.activeTab === 'click-collect') {
              this.updateAtcButton();
            }
          }, 50);
        }
      });
      const prodInfo = document.querySelector('product-form-component') || document.querySelector('.product-info') || document.body;
      observer.observe(prodInfo, { childList: true, subtree: true, characterData: true, attributes: true });

      // Intercept click events on the ATC button when it is in "Choose a store" mode
      document.addEventListener('click', (event) => {
        const atcBtn = event.target.closest('[data-testid="standalone-add-to-cart"]');
        if (atcBtn && atcBtn.dataset.ccOverrideText === 'Choose a store') {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          this.openStoreSelectorDrawer();
        }
      }, true);
    }

    updateTabStyles() {
      // console.log('[CC] updateTabStyles called, activeTab:', this.activeTab);
      this.tabTriggers.forEach((trigger) => {
        const isActive = trigger.dataset.tab === this.activeTab;
        trigger.classList.toggle('is-active', isActive);
        trigger.setAttribute('aria-selected', String(isActive));
      });

      this.panels.forEach((panel) => {
        const isPanelActive = panel.dataset.panel === this.activeTab;
        panel.classList.toggle('is-active', isPanelActive);
      });

      this.updateAtcButton();
    }

    getAtcButtons() {
      const btns = document.querySelectorAll('[data-testid="standalone-add-to-cart"]');
      return btns;
    }

    updateAtcButton() {
      const btns = this.getAtcButtons();
      if (!btns || btns.length === 0) {
        console.warn('[CC] ATC buttons NOT FOUND in DOM');
        return;
      }

      btns.forEach(btn => {
        if (btn.dataset.splusLoginRequired === 'true' || btn.dataset.splusJoinRequired === 'true') {
          return;
        }

      const isCCTab = this.activeTab === 'click-collect';
      const cookie = this.getCookie('currentStoreCC');
      const hasStore = !!cookie;
      const view = this.currentView || 'main';

      // console.log('[CC] updateAtcButton | view:', view, '| activeTab:', this.activeTab, '| hasStore:', hasStore, '| cookie:', cookie);
      // console.log('[CC] btn.disabled:', btn.disabled, '| ccManaged:', btn.dataset.ccManaged);

      // Determine what text/state the button should show
      let overrideText = null;

      if (view === 'fully-oos') {
        overrideText = 'Out of stock';
      } else if (view === 'in-store-only') {
        // In-store only: delivery + CC both unavailable, in-store stock exists
        overrideText = 'Available in store only';
      } else if (view === 'main' && isCCTab) {
        if (!hasStore) {
          // CC tab active but no store selected yet
          overrideText = 'Choose a store';
        } else {
          // A store is selected, verify it has stock
          const ccStoreCookie = this.getCookie('currentStoreCC');
          const ccStoreId = ccStoreCookie ? ccStoreCookie.dataId : null;
          const inventory = this.getCurrentInventory();
          if (ccStoreId && inventory[ccStoreId] && inventory[ccStoreId].clickCollectStatus === 'available') {
            overrideText = 'Collect in store';
          } else {
            overrideText = 'Choose a store';
          }
        }
      } else if (view === 'main' && !isCCTab) {
        // Delivery Tab active
        // If delivery is available, explicitly set 'Add to bag' and enable button,
        // because native theme might have falsely marked it as 'Out of stock' if global inventory is 0
        if (this.isDeliveryAvailable()) {
          overrideText = window.Theme?.translations?.add_to_cart || 'Add to bag';
        }
      }

      // Robustly toggle the hidden input for line item property _is_cc across all forms.
      // Only flag items that can be collected but not delivered: a variant that can also
      // be delivered never gets it, even when the customer chooses to collect it.
      const isCCOnly = isCCTab && view === 'main' && !this.isDeliveryAvailable();
      document.querySelectorAll('input.cc-property-input').forEach(input => {
        input.disabled = !isCCOnly;
      });

      if (overrideText) {
        // We cannot skip just based on dataset because the theme's morphing might change innerHTML but keep dataset!
        // Always enforce our override if we reached here.
        if (btn.dataset.ccManaged === 'true' && btn.dataset.ccOverrideText === overrideText && btn.textContent.includes(overrideText)) {
          return;
        }

        // If managed with different text, restore first then re-apply
        if (btn.dataset.ccManaged === 'true') {
          this.restoreAtcButton(btn);
        }

        const textSpan = btn.querySelector('.add-to-cart-text');
        // console.log('[CC] textSpan found:', !!textSpan, '| innerHTML:', textSpan?.innerHTML);

        if (textSpan) {
          btn.dataset.ccOriginalHtml = textSpan.innerHTML;
          textSpan.innerHTML = overrideText;
        } else {
          console.warn('[CC] .add-to-cart-text NOT FOUND — using full button innerHTML');
          btn.dataset.ccOriginalHtml = btn.innerHTML;
          btn.innerHTML = overrideText;
        }

        btn.dataset.ccOriginalDisabled = btn.disabled ? 'true' : 'false';
        btn.dataset.ccOverrideText = overrideText;
        btn.dataset.ccManaged = 'true';
        if (
          overrideText === 'Choose a store' || 
          overrideText === 'Collect in store' || 
          overrideText === 'Add to bag' || 
          overrideText === window.Theme?.translations?.add_to_cart
        ) {
          btn.disabled = false;
          btn.removeAttribute('disabled');
          btn.classList.remove('disabled', 'is-disabled');
          btn.style.pointerEvents = 'auto';
          btn.style.opacity = '1';
          btn.dataset.puppet = 'true';
        } else {
          btn.disabled = true;
          btn.style.pointerEvents = 'none';
          btn.style.opacity = '0.6';
        }
      } else {
        this.restoreAtcButton(btn);
      }
      }); // end forEach
    }

    restoreAtcButton(btn) {
      // console.log('[CC] restoreAtcButton called | ccManaged:', btn?.dataset?.ccManaged);
      if (!btn || btn.dataset.ccManaged !== 'true') {
        // console.log('[CC] restoreAtcButton: nothing to restore (ccManaged not set)');
        return;
      }

      const textSpan = btn.querySelector('.add-to-cart-text');
      // console.log('[CC] restoreAtcButton: textSpan:', textSpan, '| originalHtml:', btn.dataset.ccOriginalHtml);

      if (textSpan) {
        textSpan.innerHTML = btn.dataset.ccOriginalHtml || '';
        // console.log('[CC] Restored textSpan innerHTML');
      } else {
        btn.innerHTML = btn.dataset.ccOriginalHtml || '';
        // console.log('[CC] Restored full button innerHTML');
      }

      btn.disabled = btn.dataset.ccOriginalDisabled === 'true';
      btn.style.pointerEvents = '';
      btn.style.opacity = '';
      delete btn.dataset.ccManaged;
      delete btn.dataset.ccOriginalHtml;
      delete btn.dataset.ccOriginalDisabled;
      delete btn.dataset.ccOverrideText;
      // console.log('[CC] ATC button fully restored to normal');
    }

    async loadInventoryData() {
      // If we already have the inventory map populated, no need to fetch again
      if (this.inventoryMap && Object.keys(this.inventoryMap).length > 0) {
        return Promise.resolve();
      }

      const url = `/apps/datajet/task/${this.stockDisplayQueryId}`;
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ productId: this.productId }),
        });
        const json = await response.json();
        if (response.ok && json?.data?.product && json?.data?.locations) {
          const product = json.data.product;
          const locations = json.data.locations;

          // Cache the locations that serve the shopper's market
          this.locationsList = locations.edges.map((edge) => edge.node).filter((loc) => this.isMarketLocation(loc));
          const marketLocationIds = new Set(this.locationsList.map((loc) => loc.id));

          const isStore = (loc) => loc.isWarehouse?.value !== 'true' && loc.storeHidden?.value !== 'true';
          this.marketHasPickupStores = this.locationsList.some((loc) => isStore(loc) && loc.enableForPickup?.value === 'true');
          this.marketHasStores = this.locationsList.some((loc) => isStore(loc) && loc.storeStockAvailability?.value === 'true');

          // Build inventory maps for each variant
          this.inventoryMap = {};
          product.variants.edges.forEach(({ node: variant }) => {
            const vId = variant.id.replace('gid://shopify/ProductVariant/', '');
            const hideStockDisplay = variant.hideStockDisplay?.value === 'true';
            const locMap = {};

            variant.inventoryItem.inventoryLevels.edges.forEach(({ node: level }) => {
              const loc = level.location;
              // Stock at another market's stores doesn't count here
              if (!marketLocationIds.has(loc.id)) return;
              const locId = loc.id.replace('gid://shopify/Location/', '');
              const availableQty = level.quantities.find((q) => q.name === 'available')?.quantity ?? 0;

              if (!locMap[locId]) {
                locMap[locId] = {
                  id: locId,
                  name: loc.name,
                  totalAvailable: 0,
                };
              }
              locMap[locId].totalAvailable += availableQty;
            });

            // Make sure all locations from locationsList are in the map and populate metafields from locationsList
            this.locationsList.forEach((loc) => {
              const locId = loc.id.replace('gid://shopify/Location/', '');
              if (!locMap[locId]) {
                locMap[locId] = {
                  id: locId,
                  name: loc.name,
                  totalAvailable: 0,
                };
              }

              // Merge metafields from the full location object
              locMap[locId].frontEndName = loc.storeFrontendName?.value || loc.name;
              locMap[locId].storeHidden = loc.storeHidden?.value === 'true';
              locMap[locId].enableForPickup = loc.enableForPickup?.value === 'true';
              locMap[locId].storeEnable = loc.storeStockAvailability?.value === 'true';
              locMap[locId].isWarehouse = loc.isWarehouse?.value === 'true';
              locMap[locId].warehouseEnable = loc.warehouseStockAvailability?.value === 'true';

              // Compute statuses
              const item = locMap[locId];
              item.storeStockStatus = (!hideStockDisplay && item.storeEnable && item.totalAvailable > 0) ? 'available' : 'unavailable';
              item.clickCollectStatus = (!this.blockClickCollect && item.enableForPickup && item.totalAvailable > 0) ? 'available' : 'unavailable';
            });

            this.inventoryMap[vId] = locMap;
          });
        }
      } catch (error) {
        console.error('Click & collect fetch error:', error);
      }
    }

    // Warehouses ship to every market; stores only serve shoppers in their own country,
    // so NZ shoppers never see (or get availability from) AU stores and vice versa.
    isMarketLocation(loc) {
      if (loc.isWarehouse?.value === 'true') return true;
      const address = loc.address || {};
      if (address.countryCode && this.countryCode) {
        return address.countryCode.toUpperCase() === this.countryCode;
      }
      if (address.country && this.countryName) {
        return address.country.toLowerCase() === this.countryName;
      }
      return true;
    }

    getCurrentInventory() {
      const vId = String(this.currentVariantId || this.variantId);
      return this.inventoryMap[vId] || {};
    }

    isDeliveryAvailable() {
      const inventory = this.getCurrentInventory();

      // Fix: Only count actual warehouses (isWarehouse = true) that are NOT retail stores (enableForPickup = false)
      const warehouseLocs = Object.values(inventory).filter(loc => loc.isWarehouse && !loc.enableForPickup && loc.totalAvailable > 0);
      const hasWarehouseStock = warehouseLocs.length > 0;

      return this.currentVariantAvailable && hasWarehouseStock;
    }

    updateBlockView() {
      const inventory = this.getCurrentInventory();

      const ccLocs = Object.values(inventory).filter(loc => loc.clickCollectStatus === 'available');

      const deliveryAvailable = this.isDeliveryAvailable();
      const ccAvailable = !this.blockClickCollect && ccLocs.length > 0;
      const storeAvailable = Object.values(inventory).some((loc) => loc.storeStockStatus === 'available');
      const hasPreselectedStore = !!this.getCookie('currentStoreCC');

      const fullyOos = this.forceSoldOut || (!deliveryAvailable && !ccAvailable && !storeAvailable);
      const inStoreOnly = !deliveryAvailable && !ccAvailable && storeAvailable;

      // Hide all panels initially
      this.mainContainer.classList.add('is-hidden');
      this.fallbackInStoreOnly.classList.add('is-hidden');
      this.fallbackFullyOos.classList.add('is-hidden');
      this.alsoAvailableStoreBtn?.classList.add('is-hidden');

      // Markets without pickup stores (e.g. NZ) only offer Delivery, so drop the C&C tabs
      this.querySelectorAll('.click-and-collect__tab-trigger[data-tab="click-collect"], [data-cc-fallback-tab]').forEach((el) => {
        el.classList.toggle('is-hidden', !this.marketHasPickupStores);
      });

      if (fullyOos) {
        this.currentView = 'fully-oos';
        this.fallbackFullyOos.classList.remove('is-hidden');
        this.updateAtcButton();
      } else if (inStoreOnly) {
        this.currentView = 'in-store-only';
        this.fallbackInStoreOnly.classList.remove('is-hidden');
        this.updateAtcButton();
      } else {
        this.currentView = 'main';
        this.mainContainer.classList.remove('is-hidden');
        if (this.marketHasStores) {
          this.alsoAvailableStoreBtn?.classList.remove('is-hidden');
        }

        // Set Tab states
        // Delivery Tab
        const deliveryTabTrigger = this.querySelector('.click-and-collect__tab-trigger[data-tab="delivery"]');
        const deliverySubtitle = this.querySelector('[data-tab-delivery-subtitle]');
        if (deliveryAvailable) {
          deliveryTabTrigger.classList.remove('is-disabled');
          deliveryTabTrigger.removeAttribute('disabled');
          deliverySubtitle.textContent = deliverySubtitle.dataset.defaultSubtitle || '';
        } else {
          deliveryTabTrigger.classList.add('is-disabled');
          deliveryTabTrigger.setAttribute('disabled', 'disabled');
          deliverySubtitle.textContent = 'Out of stock';
          if (this.activeTab === 'delivery') {
            this.activeTab = 'click-collect';
          }
        }

        // Click & Collect Tab
        const ccTabTrigger = this.querySelector('.click-and-collect__tab-trigger[data-tab="click-collect"]');
        const ccSubtitle = this.querySelector('[data-tab-cc-subtitle]');
        if (ccAvailable) {
          ccTabTrigger.classList.remove('is-disabled');
          ccTabTrigger.removeAttribute('disabled');
          ccSubtitle.textContent = ccSubtitle.dataset.defaultSubtitle || '';
        } else {
          ccTabTrigger.classList.add('is-disabled');
          ccTabTrigger.setAttribute('disabled', 'disabled');
          ccSubtitle.textContent = 'Out of stock';
          if (this.activeTab === 'click-collect') {
            this.activeTab = 'delivery';
          }
        }

        this.updateTabStyles();

        // Update Click & Collect active panel details based on selected store
        this.updateCCPanelDetails();
        this.updateAtcButton();
      }
    }

    updateCCPanelDetails() {
      const cookie = this.getCookie('currentStoreCC');
      const noStoreContainer = this.querySelector('[data-cc-substate="no-store"]');
      const storeSelectedContainer = this.querySelector('[data-cc-substate="store-selected"]');

      if (cookie) {
        const inventory = this.getCurrentInventory();
        const storeData = inventory[cookie.dataId];

        const isAvailable = storeData && storeData.clickCollectStatus === 'available';

        noStoreContainer.classList.add('is-hidden');
        storeSelectedContainer.classList.remove('is-hidden');

        // Store details
        const storeNameEl = this.querySelector('[data-selected-store-name]');
        const storeStatusEl = this.querySelector('[data-selected-store-status-text]');
        const storeIconEl = this.querySelector('[data-selected-store-icon]');
        const changeStoreLinkText = this.querySelector('[data-change-store-link-text]');
        const changeStoreIconWrapper = this.querySelector('[data-change-store-link-icon-wrapper]');

        storeNameEl.textContent = `${cookie.name}`;

        if (isAvailable) {
          storeStatusEl.innerHTML = `
          <span class="color-success-text subheading-msg">In stock</span>
          <span class="subheading-dot">·</span>
          <span>Ready in 2 hours</span>`;
          storeIconEl.className = 'click-and-collect__panel-icon color-success';
          storeIconEl.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none" style="margin-top:3px;">
              <g clip-path="url(#clip0_4806_9799)">
                <path d="M7.5 13.75C10.9518 13.75 13.75 10.9518 13.75 7.5C13.75 4.04822 10.9518 1.25 7.5 1.25C4.04822 1.25 1.25 4.04822 1.25 7.5C1.25 10.9518 4.04822 13.75 7.5 13.75Z" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M5.625 7.5L6.875 8.75L9.375 6.25" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
              </g>
              <defs>
                <clipPath id="clip0_4806_9799">
                  <rect width="15" height="15" fill="white"/>
                </clipPath>
              </defs>
            </svg>
          `;

          changeStoreLinkText.textContent = 'Change store';
          changeStoreIconWrapper.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 11 11" fill="none" style="margin-right: 4px;">
            <g clip-path="url(#clip0_5488_34002)">
              <path d="M9.70456 3.12218C9.94688 2.87991 10.083 2.55131 10.0831 2.20865C10.0831 1.866 9.94705 1.53736 9.70479 1.29503C9.46252 1.05271 9.13392 0.916547 8.79126 0.916504C8.4486 0.916461 8.11997 1.05254 7.87764 1.2948L1.76072 7.4131C1.65431 7.5192 1.57561 7.64984 1.53156 7.79351L0.926099 9.78818C0.914253 9.82782 0.913359 9.86992 0.92351 9.91003C0.933661 9.95013 0.954479 9.98674 0.983756 10.016C1.01303 10.0452 1.04967 10.066 1.0898 10.076C1.12992 10.0861 1.17202 10.0852 1.21164 10.0733L3.20677 9.46826C3.3503 9.42461 3.48093 9.34639 3.58718 9.24047L9.70456 3.12218Z" stroke="currentColor" stroke-width="0.916667" stroke-linecap="round" stroke-linejoin="round"/>
            </g>
            <defs>
              <clipPath id="clip0_5488_34002">
              <rect width="11" height="11" fill="white"/>
              </clipPath>
            </defs>
            </svg>
          `;
        } else {
          storeStatusEl.innerHTML = `<span class="color-danger-text subheading-msg">Out of stock</span>`;
          storeIconEl.className = 'click-and-collect__panel-icon';
          storeIconEl.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none" style="margin-top: 2px;">
          <g clip-path="url(#clip0_4828_9079)">
            <path d="M7.5 13.75C10.9518 13.75 13.75 10.9518 13.75 7.5C13.75 4.04822 10.9518 1.25 7.5 1.25C4.04822 1.25 1.25 4.04822 1.25 7.5C1.25 10.9518 4.04822 13.75 7.5 13.75Z" stroke="#C8102E" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M9.375 5.625L5.625 9.375" stroke="#C8102E" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M5.625 5.625L9.375 9.375" stroke="#C8102E" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
          </g>
          <defs>
            <clipPath id="clip0_4828_9079">
              <rect width="15" height="15" fill="white"/>
            </clipPath>
          </defs>
        </svg>
          `;

          changeStoreLinkText.textContent = 'Try another store';
          changeStoreIconWrapper.innerHTML = `
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          `;
        }
      } else {
        noStoreContainer.classList.remove('is-hidden');
        storeSelectedContainer.classList.add('is-hidden');
      }
    }

    /* ---------------------------------------------------------------------
     * Drawer Management
     * ------------------------------------------------------------------- */
    setupDrawers() {
      if (this.storeSelectorDrawer) {
        const provSelect = this.storeSelectorDrawer.querySelector('[data-drawer-province-select]');
        const confirmBtn = this.storeSelectorDrawer.querySelector('[data-drawer-confirm-store]');

        provSelect?.addEventListener('change', () => {
          this.renderStoreSelectorList(provSelect.value);
        });

        this.initCustomSelect(provSelect);

        confirmBtn?.addEventListener('click', async () => {
          const checkedRadio = this.storeSelectorDrawer.querySelector('input[name="cc-store"]:checked');
          if (checkedRadio) {
            const storeName = checkedRadio.value;
            const locId = checkedRadio.dataset.id;
            const province = checkedRadio.dataset.province;

            // Set cookie (30 days)
            this.setCookie('currentStoreCC', { name: storeName, dataId: locId, province: province }, 30);

            // Update cart attributes
            await this.updateCartAttribute('_pickup_store_name', storeName);

            // Dispatch event
            window.dispatchEvent(
              new CustomEvent('pickupStore:updated', {
                detail: { storeName: storeName, storeId: locId, mode: 'click-collect' },
              })
            );

            // Close drawer & update view
            // Cookie already set above via setCookie — updateAtcButton will now see hasStore=true
            // console.log('[CC] Store confirmed:', storeName, '| locId:', locId, '| cookie now set');
            this.storeSelectorDrawer.closeDialog();
            this.updateBlockView();
            // updateBlockView → updateTabStyles → updateAtcButton handles restore via cookie check
          }
        });
      }

      if (this.instoreDrawer) {
        const provSelect = this.instoreDrawer.querySelector('[data-drawer-instore-province-select]');
        provSelect?.addEventListener('change', () => {
          this.renderInstoreList(provSelect.value);
        });

        this.initCustomSelect(provSelect);
      }
    }

    initCustomSelect(selectEl) {
      if (!selectEl) return;
      const wrapper = selectEl.closest('[data-custom-select-container]');
      if (!wrapper) return;

      const trigger = wrapper.querySelector('[data-custom-select-trigger]');
      
      // Toggle dropdown open/closed
      trigger?.addEventListener('click', (e) => {
        e.stopPropagation();
        
        // Close other custom selects first
        this.querySelectorAll('[data-custom-select-container]').forEach((otherWrapper) => {
          if (otherWrapper !== wrapper) {
            otherWrapper.classList.remove('is-open');
          }
        });

        wrapper.classList.toggle('is-open');
      });

      // We should close it on clicks outside
      if (!this._hasGlobalClickClose) {
        document.addEventListener('click', (e) => {
          this.querySelectorAll('[data-custom-select-container]').forEach((w) => {
            if (!w.contains(e.target)) {
              w.classList.remove('is-open');
            }
          });
        });
        this._hasGlobalClickClose = true;
      }
    }

    updateCustomSelect(selectEl) {
      if (!selectEl) return;
      const wrapper = selectEl.closest('[data-custom-select-container]');
      if (!wrapper) return;

      const triggerTextEl = wrapper.querySelector('.custom-select__trigger-text');
      const optionsContainer = wrapper.querySelector('[data-custom-select-options]');
      if (!triggerTextEl || !optionsContainer) return;

      // Update trigger text
      const selectedOption = selectEl.options[selectEl.selectedIndex];
      triggerTextEl.textContent = selectedOption ? selectedOption.textContent : selectEl.placeholder || 'Select state';

      if (selectEl.value === '') {
        triggerTextEl.classList.add('is-placeholder');
      } else {
        triggerTextEl.classList.remove('is-placeholder');
      }

      // Rebuild custom options
      optionsContainer.innerHTML = '';
      Array.from(selectEl.options).forEach((opt) => {

        if (opt.value === "") return;

        const customOpt = document.createElement('div');
        customOpt.className = 'custom-select__option';
        if (opt.value === selectEl.value) {
          customOpt.classList.add('is-selected');
        }
        customOpt.textContent = opt.textContent;
        customOpt.dataset.value = opt.value;

        customOpt.addEventListener('click', (e) => {
          e.stopPropagation();
          selectEl.value = opt.value;
          // Dispatch change event to trigger the native change listener
          selectEl.dispatchEvent(new Event('change'));
          wrapper.classList.remove('is-open');
          this.updateCustomSelect(selectEl);
        });

        optionsContainer.appendChild(customOpt);
      });
    }

    openStoreSelectorDrawer() {
      if (!this.storeSelectorDrawer) return;

      const provSelect = this.storeSelectorDrawer.querySelector('[data-drawer-province-select]');
      this.populateProvinceSelect(provSelect);

      const cookie = this.getCookie('currentStoreCC');
      if (cookie && cookie.province) {
        provSelect.value = cookie.province;
      } else {
        provSelect.value = '';
      }
      this.updateCustomSelect(provSelect);

      this.renderStoreSelectorList(provSelect.value);
      this.storeSelectorDrawer.showDialog();
    }

    openInstoreDrawer() {
      if (!this.instoreDrawer) return;

      const provSelect = this.instoreDrawer.querySelector('[data-drawer-instore-province-select]');
      this.populateProvinceSelect(provSelect);

      const cookie = this.getCookie('currentStoreCC') || this.getCookie('currentStoreInStore');
      if (cookie && cookie.province) {
        provSelect.value = cookie.province;
      } else {
        provSelect.value = '';
      }
      this.updateCustomSelect(provSelect);

      this.renderInstoreList(provSelect.value);
      this.instoreDrawer.showDialog();
    }

    updateOpenDrawers() {
      // Re-render if drawer is currently open on variant change
      if (this.storeSelectorDrawer?.refs?.dialog?.open) {
        const provSelect = this.storeSelectorDrawer.querySelector('[data-drawer-province-select]');
        this.renderStoreSelectorList(provSelect.value);
      }
      if (this.instoreDrawer?.refs?.dialog?.open) {
        const provSelect = this.instoreDrawer.querySelector('[data-drawer-instore-province-select]');
        this.renderInstoreList(provSelect.value);
      }
    }

    populateProvinceSelect(selectEl) {
      if (!selectEl) return;
      const uniqueProvinces = [...new Set(this.locationsList.map((loc) => loc.address?.province).filter(Boolean))].sort();

      selectEl.innerHTML = '<option value="">Select state</option>';
      uniqueProvinces.forEach((prov) => {
        const opt = document.createElement('option');
        opt.value = prov;
        opt.textContent = prov;
        selectEl.appendChild(opt);
      });

      this.updateCustomSelect(selectEl);
    }

    async renderStoreSelectorList(province) {
      const container = this.storeSelectorDrawer.querySelector('[data-drawer-store-list-container]');
      const listEl = this.storeSelectorDrawer.querySelector('[data-drawer-store-list]');
      const confirmBtn = this.storeSelectorDrawer.querySelector('[data-drawer-confirm-store]');
      const warningEl = this.storeSelectorDrawer.querySelector('[data-drawer-warning]');

      confirmBtn.setAttribute('disabled', 'disabled');
      confirmBtn.textContent = 'Select store';
      warningEl.classList.add('is-hidden');

      if (!province) {
        container.classList.add('is-hidden');
        listEl.innerHTML = '';
        return;
      }

      container.classList.remove('is-hidden');
      listEl.innerHTML = '<p class="click-and-collect-drawer__loading">Loading stores...</p>';

      const inventory = this.getCurrentInventory();
      const filtered = this.locationsList.filter((loc) => {
        const isWarehouse = loc.isWarehouse?.value === 'true';
        const isHidden = loc.storeHidden?.value === 'true';
        const isEnable = loc.storeStockAvailability?.value === 'true';
        return loc.address?.province === province && !isWarehouse && !isHidden && isEnable;
      });

      // Sort so available stores show at the top
      filtered.sort((a, b) => {
        const aId = a.id.replace('gid://shopify/Location/', '');
        const bId = b.id.replace('gid://shopify/Location/', '');
        const aAvail = (inventory[aId] && inventory[aId].clickCollectStatus === 'available') ? 1 : 0;
        const bAvail = (inventory[bId] && inventory[bId].clickCollectStatus === 'available') ? 1 : 0;
        return bAvail - aAvail;
      });

      const selectedCookie = this.getCookie('currentStoreCC');
      const selectedId = selectedCookie?.dataId || '';

      const cart = await this.getCartData();

      if (filtered.length === 0) {
        listEl.innerHTML = '<p class="click-and-collect-drawer__no-results">No stores found in this state.</p>';
        return;
      }

      listEl.innerHTML = '';

      filtered.forEach((loc) => {
        const locId = loc.id.replace('gid://shopify/Location/', '');
        const isSelected = locId === selectedId;
        const storeData = inventory[locId];
        const isAvailable = storeData && storeData.clickCollectStatus === 'available';

        const statusClass = isAvailable ? 'in-stock' : 'out-of-stock';
        const statusText = isAvailable ? 'In stock' : 'Out of stock';

        const card = document.createElement('label');
        card.className = `click-and-collect-drawer__store-card ${isSelected ? 'is-selected' : ''} ${isAvailable ? '' : 'is-oos'}`;
        card.dataset.storeCardId = locId;

        const addressStr = this.formatAddress(loc.address);

        card.innerHTML = `
          <input type="radio" name="cc-store" value="${loc.storeFrontendName?.value || loc.name}" data-id="${locId}" data-province="${loc.address.province}" ${isSelected ? 'checked' : ''} ${isAvailable ? '' : 'disabled'} class="click-and-collect-drawer__radio">
          <div class="click-and-collect-drawer__store-card-content">
            <span class="click-and-collect-drawer__store-card-name">${loc.storeFrontendName?.value || loc.name}</span>
            <span class="click-and-collect-drawer__store-card-address">${addressStr}</span>
            <span class="click-and-collect-drawer__store-card-status ${statusClass}">
              <span class="click-and-collect-drawer__status-dot"></span>
              ${statusText}
            </span>
          </div>
        `;

        card.querySelector('input').addEventListener('change', (e) => {
          // Highlight card
          this.storeSelectorDrawer.querySelectorAll('.click-and-collect-drawer__store-card').forEach((c) => {
            c.classList.remove('is-selected');
          });
          card.classList.add('is-selected');

          confirmBtn.removeAttribute('disabled');
          confirmBtn.textContent = 'Confirm store';

          // Check cart warning
          const selectedName = e.target.value;
          this.checkCartWarning(cart, selectedName);
        });

        listEl.appendChild(card);

        if (isSelected && isAvailable) {
          confirmBtn.removeAttribute('disabled');
          confirmBtn.textContent = 'Confirm store';
        }
      });
    }

    renderInstoreList(province) {
      const container = this.instoreDrawer.querySelector('[data-drawer-instore-store-list-container]');
      const listEl = this.instoreDrawer.querySelector('[data-drawer-instore-store-list]');

      if (!province) {
        container.classList.add('is-hidden');
        listEl.innerHTML = '';
        return;
      }

      container.classList.remove('is-hidden');
      listEl.innerHTML = '';

      const inventory = this.getCurrentInventory();
      const filtered = this.locationsList.filter((loc) => {
        const isWarehouse = loc.isWarehouse?.value === 'true';
        const isHidden = loc.storeHidden?.value === 'true';
        const isEnable = loc.storeStockAvailability?.value === 'true';
        return loc.address?.province === province && !isWarehouse && !isHidden && isEnable;
      });

      // Sort so available stores show at the top
      filtered.sort((a, b) => {
        const aId = a.id.replace('gid://shopify/Location/', '');
        const bId = b.id.replace('gid://shopify/Location/', '');
        const aAvail = (inventory[aId] && inventory[aId].storeStockStatus === 'available') ? 1 : 0;
        const bAvail = (inventory[bId] && inventory[bId].storeStockStatus === 'available') ? 1 : 0;
        return bAvail - aAvail;
      });

      if (filtered.length === 0) {
        listEl.innerHTML = '<p class="click-and-collect-drawer__no-results">No stores found in this state.</p>';
        return;
      }

      filtered.forEach((loc) => {
        const locId = loc.id.replace('gid://shopify/Location/', '');
        const storeData = inventory[locId];
        const isAvailable = storeData && storeData.storeStockStatus === 'available';

        const statusClass = isAvailable ? 'in-stock' : 'out-of-stock';
        const statusText = isAvailable ? 'In stock' : 'Out of stock';

        const card = document.createElement('div');
        card.className = `click-and-collect-drawer__store-card read-only ${isAvailable ? '' : 'is-oos'}`;

        const addressStr = this.formatAddress(loc.address);

        card.innerHTML = `
          <div class="click-and-collect-drawer__store-card-content">
            <span class="click-and-collect-drawer__store-card-name">${loc.storeFrontendName?.value || loc.name}</span>
            <span class="click-and-collect-drawer__store-card-address">${addressStr}</span>
            <span class="click-and-collect-drawer__store-card-status ${statusClass}">
              <span class="click-and-collect-drawer__status-dot"></span>
              ${statusText}
            </span>
          </div>
        `;

        listEl.appendChild(card);
      });
    }

    checkCartWarning(cart, selectedStoreName) {
      const warningEl = this.storeSelectorDrawer.querySelector('[data-drawer-warning]');
      const warningSubtextEl = this.storeSelectorDrawer.querySelector('[data-drawer-warning-subtext]');

      if (cart && cart.item_count > 0 && cart.attributes?._pickup_store_name) {
        const existingStore = cart.attributes._pickup_store_name;
        if (existingStore && existingStore !== selectedStoreName) {
          warningSubtextEl.textContent = `You have click & collect items in your cart from ${existingStore}. Switching to another store will update those items.`;
          warningEl.classList.remove('is-hidden');
          return;
        }
      }
      warningEl.classList.add('is-hidden');
    }

    formatAddress(address) {
      if (!address) return '';
      const parts = [
        address.address1,
        address.address2,
        address.city,
        address.province,
        address.zip
      ].filter(Boolean);
      return parts.join(', ');
    }

    async getCartData() {
      try {
        const res = await fetch('/cart.js');
        if (res.ok) {
          return await res.json();
        }
      } catch (err) {
        console.error('Error fetching cart data:', err);
      }
      return null;
    }

    async updateCartAttribute(key, value) {
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { [key]: value || '' } }),
        });
      } catch (err) {
        console.error('Error updating cart attribute:', err);
      }
    }

    /* ---------------------------------------------------------------------
     * Cookie Helpers
     * ------------------------------------------------------------------- */
    setCookie(name, value, days = 7) {
      const expires = new Date();
      expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
      document.cookie = `${name}=${encodeURIComponent(JSON.stringify(value))};expires=${expires.toUTCString()};path=/`;
    }

    getCookie(name) {
      const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
      if (!match) return null;
      try {
        return JSON.parse(decodeURIComponent(match[1]));
      } catch {
        return null;
      }
    }
  }

  customElements.define('click-and-collect-block', ClickAndCollectBlock);
})();